import { useEffect, useState, useRef, useContext } from "react";
import {
  Min_MinDetSize,
  Max_MinDetSize,
  personKey,
  vehicleKey,
  onMotorVehicleKey,
} from "lib/constants";
import { SvgAdd, SvgChecked, SvgSnapshotRefresh } from "ui/svgs";
import { SvgLogo, SvgToastInfo } from "ui/svgs";
import CameraRuleEditPlayer from "ui/automation/camera-rule-edit-player";
import { CameraAlertDetection } from "lib/types";
import { DbPicDataType } from "lib/types";
import Slider from "ui/components/slider";
import RegionControlBtn from "ui/automation/region-control_btn";
import { SignalContext } from "context/SignalContext";
import clsx from "clsx";
import { DB_STORE } from "lib/constants";
import { Tooltip } from "antd";
import { useCommonService } from "pages/service";
import type { SnapshotDataTypes } from "pages/service";
import { getAlertIcon } from "ui/components/common";
import { softTitle } from "lib/utils";

export default function Regions({
  streamingCamera,
  setCameraAlertDetections,
  cameraAlertDetections,
  supportedEventTypes,
}) {
  const { takeSnapshot } = useCommonService();
  const { getDb } = useContext(SignalContext);
  const cameraIdRef = useRef(0);
  const [regionIndex, setRegionIndex] = useState(-1);

  const [minPersonDetSize, setMinPersonDetSize] = useState(
    Max_MinDetSize * 0.2,
  );
  const [minVehicleDetSize, setMinVehicleDetSize] = useState(
    Max_MinDetSize * 0.2,
  );
  const [minMotorVehicleDetSize, setMinMotorVehicleDetSize] = useState(
    Max_MinDetSize * 0.2,
  );

  const [snapshot, setSnapshot] = useState<SnapshotDataTypes>(null);
  const [manualSnapshotLoading, setManualSnapshotLoading] = useState(false);

  const canvasContainerRef = useRef(null);

  useEffect(() => {
    console.log("first effect ", cameraAlertDetections);
    for (let i = 0; i < cameraAlertDetections.length; i++) {
      for (let j = 0; j < cameraAlertDetections[i].objects.length; j++) {
        const obj = cameraAlertDetections[i].objects[j];
        updateMinSize(obj.type, obj.minDetectionWidth);
      }
    }

    if (cameraAlertDetections.length > 0 && regionIndex < 0) {
      setRegionIndex(0);
    }
    if (cameraAlertDetections.length == 0) {
      setRegionIndex(-1);
    }
  }, [cameraAlertDetections]);

  useEffect(() => {
    if (cameraAlertDetections.length > 0) {
      setRegionIndex(0);
    }
    setSnapshot(null);
  }, [streamingCamera]);

  useEffect(() => {
    if (!streamingCamera) {
      return;
    }
    setSnapshot(null);
    startFechSnapshot();
  }, [streamingCamera]);

  const startFechSnapshot = (force = false) => {
    let streamId = 0;
    if (streamingCamera?.capabilities?.streamNum > 1) streamId = 1;
    console.log("doFetchSnapshot", +streamingCamera?.cameraId);
    cameraIdRef.current = +streamingCamera?.cameraId;
    doFetchSnapshot(
      {
        streamId: streamId,
        remoteIndex: +streamingCamera?.remoteIndex,
        id: +streamingCamera?.cameraId,
        macAddress: streamingCamera?.cameraMac,
        ...streamingCamera,
      },
      force,
    );
  };

  const doFetchSnapshot = (camera, force) => {
    takeSnapshot(
      [camera],
      streamingCamera?.orgId,
      (snapshots: SnapshotDataTypes[]) => {
        setSnapshot((pre) => {
          if (snapshots?.at(0)?.id == cameraIdRef.current) {
            setManualSnapshotLoading(false);
            return snapshots?.at(0);
          }
          return pre;
        });
      },
      force,
    );
  };

  useEffect(() => {
    if (!streamingCamera) {
      return;
    }
    getDb().then((db) => {
      db.read(DB_STORE.SNAPSHOT, +streamingCamera.cameraId).then(
        (data: DbPicDataType) => {
          setSnapshot({
            blob: data?.imageBlob,
            id: +streamingCamera.cameraId,
            width: data?.width,
            height: data?.height,
          });
          console.log("Read data", data);
        },
      );
    });
  }, [streamingCamera]);

  function updateMinSize(type: string, value: number) {
    switch (type) {
      case personKey:
        setMinPersonDetSize(value);
        break;
      case vehicleKey:
        setMinVehicleDetSize(value);
        break;
      case onMotorVehicleKey:
        setMinMotorVehicleDetSize(value);
        break;
    }
  }

  function updateRegionIndex(detections) {
    detections?.forEach((detection, index) => {
      detection.index = index;
    });
  }

  function addRegion() {
    const minDeteSize = [];
    supportedEventTypes.forEach((type) => {
      const size =
        type === personKey
          ? minPersonDetSize
          : type === vehicleKey
            ? minVehicleDetSize
            : minMotorVehicleDetSize;
      minDeteSize.push({
        minDetectionHeight: size,
        minDetectionWidth: size,
        type,
      });
    });
    const detection: CameraAlertDetection = {
      objects: minDeteSize,
      region: {
        polygon: { sensitivity: 60, timeThreshold: 6 },
        type: "polygon",
      },
    };
    const detections = [...cameraAlertDetections, detection];
    updateRegionIndex(detections);
    setCameraAlertDetections(detections);
    setRegionIndex(cameraAlertDetections.length);
  }

  function removeRegion(i: number) {
    const detections = [
      ...cameraAlertDetections.filter((_, index) => index !== i),
    ];
    updateRegionIndex(detections);
    setCameraAlertDetections(detections);

    setRegionIndex(detections.length - 1);
  }

  function updateRegion(points: { x: number; y: number }[]) {
    cameraAlertDetections[regionIndex].region.polygon.points = points;
    setCameraAlertDetections([...cameraAlertDetections]);
  }

  function updateRegionSensitity(value: number) {
    cameraAlertDetections[regionIndex].region.polygon.sensitivity = value;
    setCameraAlertDetections([...cameraAlertDetections]);
  }

  function updateRegionTimeThreshold(value: number) {
    cameraAlertDetections[regionIndex].region.polygon.timeThreshold = value;
    setCameraAlertDetections([...cameraAlertDetections]);
  }

  function algEnableClcked(type: string) {
    const indexType = cameraAlertDetections[regionIndex].objects.findIndex(
      (obj) => obj.type === type,
    );
    if (indexType >= 0) {
      cameraAlertDetections[regionIndex].objects.splice(indexType, 1);
    } else {
      const size =
        type === personKey
          ? minPersonDetSize
          : type === vehicleKey
            ? minVehicleDetSize
            : minMotorVehicleDetSize;
      cameraAlertDetections[regionIndex].objects.push({
        type: type,
        minDetectionHeight: size,
        minDetectionWidth: size,
      });
      const customOrder = [onMotorVehicleKey, personKey, vehicleKey];
      cameraAlertDetections[regionIndex].objects.sort(
        (a, b) => customOrder.indexOf(a.type) - customOrder.indexOf(b.type),
      );
    }
    setCameraAlertDetections([...cameraAlertDetections]);
  }

  return (
    <div className="w-full flex flex-col flex-grow pt-6 ">
      {streamingCamera?.capabilities?.objectSize && (
        <>
          <div className="pb-2 h-8">Minimal Detection Size </div>
          <div className="flex gap-6 flex-wrap justify-between2 pb-6">
            {[personKey, vehicleKey, onMotorVehicleKey].map((type) => {
              return (
                supportedEventTypes?.includes(type) && (
                  <div key={type} className="flex items-center gap-2.5">
                    {getAlertIcon(type, true, "fill-neutral-800")}
                    <Slider
                      cur={
                        type === personKey
                          ? minPersonDetSize
                          : type === vehicleKey
                            ? minVehicleDetSize
                            : minMotorVehicleDetSize
                      }
                      min={Min_MinDetSize}
                      max={Max_MinDetSize}
                      marks={5}
                      onChange={(value: number) => {
                        updateMinSize(type, value);
                        cameraAlertDetections.forEach((detection) => {
                          const index = detection.objects.findIndex(
                            (obj) => obj.type === type,
                          );
                          if (index >= 0) {
                            detection.objects[index].minDetectionHeight = value;
                            detection.objects[index].minDetectionWidth = value;
                          }
                        });
                        setCameraAlertDetections([...cameraAlertDetections]);
                        console.log("--new value", value);
                      }}
                    />
                  </div>
                )
              );
            })}
          </div>
        </>
      )}
      <div className="flex overflow-hidden px-1">
        <div className="flex-grow overflow-hidden min-w-[200px]">
          <div className="flex gap-1 flex-wrap">
            {cameraAlertDetections?.map((_, index) => (
              <RegionControlBtn
                active={regionIndex === index}
                showDelete={true}
                index={index}
                key={index}
                title="Region "
                onChange={setRegionIndex}
                onRemove={removeRegion}
              />
            ))}
            <div className="flex-grow">
              {supportedEventTypes?.length > 0 &&
                cameraAlertDetections?.length < 4 && (
                  <button
                    className="px-2 py-2 fill-zinc--500 hover:fill-zinc-400"
                    onClick={addRegion}
                  >
                    <SvgAdd width="14" />
                  </button>
                )}
            </div>

            <div className=" w-8 h-8 flex items-center justify-center ">
              {manualSnapshotLoading ? (
                <div
                  className="ml-1 text-[#222222]  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : (
                <Tooltip title="Refresh Snapshot">
                  <button
                    className="w-8 h-8 rounded-full fill-[#222222] flex justify-center items-center cursor-pointer hover:fill-[#585757]"
                    onClick={(e) => {
                      setManualSnapshotLoading(true);
                      startFechSnapshot(true);
                    }}
                  >
                    <SvgSnapshotRefresh width="16" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          <div
            className="relative w-full max-h-full max-w-full aspect-video flex item-center justify-center"
            ref={canvasContainerRef}
          >
            {streamingCamera ? (
              <CameraRuleEditPlayer
                type="regions"
                cameraId={streamingCamera.cameraId}
                streamingCamera={streamingCamera}
                supportedEventTypes={supportedEventTypes}
                ruleIndex={regionIndex}
                intrusionDetection={cameraAlertDetections[regionIndex]?.region}
                updateRegionDetection={updateRegion}
                minPersonDetSize={minPersonDetSize}
                minVehicleDetSize={minVehicleDetSize}
                minMotorVehicleDetSize={minMotorVehicleDetSize}
                objectSizeEnable={streamingCamera.capabilities?.objectSize}
                snapshot={
                  snapshot?.id == +streamingCamera?.cameraId ? snapshot : null
                }
              />
            ) : (
              <div className="aspect-video bg-zinc-100 w-full h-full  border-transparent flex flex-col items-center justify-center">
                <SvgLogo width="60" className="fill-neutral-400 mb-3" />
                <p className="text-neutral-400 font-semibold text-2xl">
                  {softTitle()}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="w-[450px] basis-[450px] flex-shrink-0 pl-6 flex items-center flex-col justify-center">
          <div className="flex h-9 mt-5 w-full max-w-full items-center">
            <div className="w-[120px] text-left">Algorithm </div>
            {supportedEventTypes?.map((type, index) => {
              const keys = [personKey, vehicleKey, onMotorVehicleKey];
              for (let i = 0; i < keys.length; i++) {
                if (type === keys[i]) {
                  const checked =
                    cameraAlertDetections[regionIndex]?.objects?.findIndex(
                      (obj) => obj.type === keys[i],
                    ) >= 0;
                  return (
                    <div
                      key={type}
                      className={clsx(
                        "flex h-full w-24 items-center justify-center border border-zinc-200 hover:opacity-80",
                        index == 0 ? "rounded-s-full" : "rounded-none",
                        index == supportedEventTypes.length - 1
                          ? "rounded-r-full"
                          : "rounded-none",
                        checked ? "bg-blue-100 gap-1.5" : "white",
                        regionIndex >= 0
                          ? "cursor-pointer"
                          : "cursor-not-allowed",
                      )}
                      onClick={() => {
                        if (regionIndex >= 0) {
                          algEnableClcked(type);
                        }
                      }}
                    >
                      {checked ? (
                        <SvgChecked width="15" className="fill-zinc-400" />
                      ) : null}
                      {getAlertIcon(type, true, "fill-neutral-800")}
                    </div>
                  );
                }
              }
            })}
            {supportedEventTypes.length <= 0 ? "-" : null}
          </div>
          {supportedEventTypes.length > 0 &&
            streamingCamera?.capabilities?.timeThreshold && (
              <div className="mt-2 flex h-9 w-full max-w-full items-center">
                <span className="w-[120px] text-left"> Time Threshold</span>
                <Slider
                  cur={
                    cameraAlertDetections[regionIndex]?.region.polygon
                      ?.timeThreshold
                  }
                  min={1}
                  max={10}
                  marks={10}
                  width={278}
                  disabled={regionIndex < 0}
                  onChange={(value: number) => {
                    updateRegionTimeThreshold(value);
                    console.log("--new value", value);
                  }}
                />
                <div className="w-6 ml-2">
                  {
                    cameraAlertDetections?.at(regionIndex)?.region?.polygon
                      ?.timeThreshold
                  }
                  s
                </div>
              </div>
            )}

          <div className="mt-2 flex h-9 w-full max-w-[450px]  items-center">
            <div className="flex items-center gap-1 cursor-pointer w-[120px] text-left">
              Sensitivity
              <Tooltip title="Higher sensitivity settings will result in more alerts being triggered.">
                <div>
                  <SvgToastInfo width="16" className="fill-[#717171]" />
                </div>
              </Tooltip>
            </div>
            {supportedEventTypes.length > 0 && (
              <Slider
                cur={
                  cameraAlertDetections[regionIndex]?.region.polygon
                    ?.sensitivity
                }
                min={20}
                max={100}
                marks={5}
                width={278}
                disabled={regionIndex < 0}
                onChange={(value: number) => {
                  updateRegionSensitity(value);
                  console.log("--new value", value);
                }}
              />
            )}
            {supportedEventTypes.length <= 0 ? "-" : null}
          </div>
          {/* <div className="mt-2 flex h-9 w-[450px] items-center">
          <span className=" w-[120px] text-left"> Auto Track PTZ</span>

          <Switch
            onChange={() => {}}
            checked={true}
            uncheckedIcon={false}
            checkedIcon={false}
            onColor="#3E83F6"
            offColor="#fff"
            handleDiameter={20}
            offHandleColor="#9E9E9E"
            className="border border-zinc-200"
            height={32}
            width={52}
          />
        </div> */}
        </div>
      </div>
    </div>
  );
}
